import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertCircle, CreditCard, Loader2, Receipt, RotateCcw, X } from 'lucide-react'
import { toast } from 'sonner'
import { Badge } from '@quebon/ui/components/badge'
import { handleApiError } from '@/api/client'
import {
  lcContentFeeApi,
  type OrganizationContentFeeOrder,
} from '@/api/lcContentFee'

interface Props {
  organizationId: number
}

const STATUS_LABELS: Record<string, string> = {
  active: '결제 완료',
  refunded: '환불 완료',
  superseded: '이전 결제',
}

function statusClass(status: string): string {
  if (status === 'active') return 'border-emerald-200 bg-emerald-50 text-emerald-700'
  if (status === 'refunded') return 'border-rose-200 bg-rose-50 text-rose-700'
  return 'border-slate-200 bg-slate-100 text-slate-600'
}

function formatAmount(amount: number | null): string {
  return amount == null ? '-' : `${amount.toLocaleString('ko-KR')}원`
}

function formatDate(value: string | null): string {
  if (!value) return '-'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('ko-KR')
}

function formatCard(last4: string | null): string {
  return last4 ? `**** ${last4}` : '-'
}

function formatProvider(provider: string | null, amount?: number | null): string {
  if (amount === 0) return '무료'
  if (!provider || provider === 'manual') return '수기'
  if (provider === 'billgate') return 'Billgate'
  if (provider === 'toss') return 'Toss'
  return provider
}

export function OrganizationPaymentHistoryTab({ organizationId }: Props) {
  const queryClient = useQueryClient()
  const queryKey = ['admin', 'lc-content-fee', 'organizations', organizationId, 'orders'] as const
  const [refundTarget, setRefundTarget] = useState<OrganizationContentFeeOrder | null>(null)
  const [refundReason, setRefundReason] = useState('')

  const { data: orders = [], isLoading, isError } = useQuery({
    queryKey,
    queryFn: async () => (await lcContentFeeApi.getOrganizationOrders(organizationId)).data.data ?? [],
  })

  const refundMutation = useMutation({
    mutationFn: ({ entitlementId, memo }: { entitlementId: number; memo: string | null }) =>
      lcContentFeeApi.refundOrganizationOrder(organizationId, entitlementId, { memo }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
      setRefundTarget(null)
      setRefundReason('')
      toast.success('결제 환불과 레슨 회수가 완료되었습니다.')
    },
    onError: (error) => toast.error(`환불에 실패했습니다. ${handleApiError(error)}`),
  })

  const openRefund = (order: OrganizationContentFeeOrder) => {
    setRefundTarget(order)
    setRefundReason('')
  }

  const closeRefund = () => {
    if (refundMutation.isPending) return
    setRefundTarget(null)
    setRefundReason('')
  }

  const submitRefund = () => {
    if (!refundTarget) return
    const memo = refundReason.trim()
    refundMutation.mutate({ entitlementId: refundTarget.entitlementId, memo: memo || null })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-500">
        <Loader2 className="size-5 animate-spin" />
        결제 내역을 불러오는 중입니다.
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center gap-2 rounded-lg border border-rose-200 bg-rose-50 py-12 text-sm text-rose-700">
        <AlertCircle className="size-5" />
        결제 내역을 불러오지 못했습니다.
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h3 className="text-base font-semibold text-slate-900">콘텐츠 사용료 결제내역</h3>
        <p className="mt-1 text-sm text-slate-500">
          이 LC 기관의 결제와 환불 이력을 확인하고 활성 결제를 전액 환불할 수 있습니다.
        </p>
      </div>

      {orders.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-slate-300 py-20 text-slate-400">
          <Receipt className="size-10" />
          <p className="text-sm">결제 내역이 없습니다.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="min-w-[1040px] w-full text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs font-medium text-slate-500">
              <tr>
                <th className="px-4 py-3">주문</th>
                <th className="px-4 py-3">원생</th>
                <th className="px-4 py-3">상품</th>
                <th className="px-4 py-3 text-right">레슨</th>
                <th className="px-4 py-3 text-right">결제금액</th>
                <th className="px-4 py-3">결제수단</th>
                <th className="px-4 py-3">상태</th>
                <th className="px-4 py-3">결제일시</th>
                <th className="px-4 py-3 text-right">작업</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {orders.map((order) => (
                <tr key={order.entitlementId} className="text-slate-700">
                  <td className="px-4 py-3 font-mono text-xs">{order.orderId ?? '-'}</td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-slate-900">
                      {order.beneficiaryName || `원생 #${order.beneficiaryUserId}`}
                    </p>
                    <p className="mt-0.5 text-xs text-slate-500">{order.beneficiaryEmail || '-'}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-slate-900">
                      {order.contentTitle || '커리큘럼 정보 없음'}
                    </p>
                    <Badge className="mt-1" variant="secondary">{order.level}</Badge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <p>{order.grantedLessons}회 지급</p>
                    <p className="mt-0.5 text-xs text-slate-500">
                      {order.usedLessons}회 사용 · {order.remainingLessons}회 잔여
                    </p>
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-slate-900">
                    {formatAmount(order.amount)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <CreditCard className="size-4 text-slate-400" />
                      <span>{formatProvider(order.pgProvider, order.amount)}</span>
                    </div>
                    <p className="mt-0.5 text-xs text-slate-500">{formatCard(order.cardLast4Digits)}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${statusClass(order.status)}`}>
                      {STATUS_LABELS[order.status] || order.status}
                    </span>
                    {order.refundedAt ? (
                      <p className="mt-1 text-xs text-slate-500">{formatDate(order.refundedAt)}</p>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-xs">{formatDate(order.paidAt)}</td>
                  <td className="px-4 py-3 text-right">
                    {order.status === 'active' ? (
                      <button
                        type="button"
                        onClick={() => openRefund(order)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 px-3 py-1.5 text-xs font-medium text-rose-700 transition-colors hover:bg-rose-50"
                      >
                        <RotateCcw className="size-3.5" />
                        환불
                      </button>
                    ) : (
                      <span className="text-xs text-slate-400">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {refundTarget ? (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/60 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="content-fee-refund-title"
          onClick={closeRefund}
        >
          <div className="w-full max-w-lg rounded-xl bg-white shadow-xl" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
              <h2 id="content-fee-refund-title" className="text-lg font-semibold text-slate-900">콘텐츠 사용료 환불</h2>
              <button
                type="button"
                onClick={closeRefund}
                disabled={refundMutation.isPending}
                aria-label="닫기"
                className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 disabled:opacity-50"
              >
                <X className="size-5" />
              </button>
            </div>

            <div className="flex flex-col gap-5 px-6 py-5">
              <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
                <p className="font-semibold">이 작업은 되돌릴 수 없습니다.</p>
                <p className="mt-1 leading-6">
                  {formatAmount(refundTarget.amount)} 전액을 환불하고 수강권을 무효화합니다.
                  이번 결제로 지급한 {refundTarget.grantedLessons}회 범위에서 현재 잔여
                  {` ${refundTarget.remainingLessons}`}회까지만 회수하여 잔여값이 0 미만이 되지 않게 처리합니다.
                </p>
                <p className="mt-1 leading-6">
                  {refundTarget.pgProvider === 'billgate' || refundTarget.pgProvider === 'toss'
                    ? `${formatProvider(refundTarget.pgProvider, refundTarget.amount)}에도 결제 취소를 요청하며, PG 취소가 실패하면 내부 환불도 처리하지 않습니다.`
                    : '수기·무료 결제는 외부 PG 취소 없이 내부 결제와 수강권만 환불 처리합니다.'}
                </p>
              </div>

              <dl className="grid grid-cols-[7rem_1fr] gap-x-3 gap-y-2 text-sm">
                <dt className="text-slate-500">원생</dt>
                <dd className="font-medium text-slate-900">{refundTarget.beneficiaryName || `#${refundTarget.beneficiaryUserId}`}</dd>
                <dt className="text-slate-500">주문번호</dt>
                <dd className="font-mono text-slate-700">{refundTarget.orderId ?? '-'}</dd>
                <dt className="text-slate-500">결제일시</dt>
                <dd className="text-slate-700">{formatDate(refundTarget.paidAt)}</dd>
              </dl>

              <label className="flex flex-col gap-2 text-sm font-medium text-slate-700">
                <span>
                  환불 사유 <span className="font-normal text-slate-400">(선택)</span>
                </span>
                <textarea
                  value={refundReason}
                  onChange={(event) => setRefundReason(event.target.value)}
                  maxLength={500}
                  rows={3}
                  placeholder="운영 기록에 남길 환불 사유를 입력하세요."
                  className="resize-none rounded-lg border border-slate-300 px-3 py-2 font-normal text-slate-900 outline-none focus:border-rose-400 focus:ring-2 focus:ring-rose-100"
                />
              </label>
            </div>

            <div className="flex justify-end gap-2 border-t border-slate-200 px-6 py-4">
              <button
                type="button"
                onClick={closeRefund}
                disabled={refundMutation.isPending}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                취소
              </button>
              <button
                type="button"
                onClick={submitRefund}
                disabled={refundMutation.isPending}
                className="inline-flex items-center gap-2 rounded-lg bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {refundMutation.isPending ? <Loader2 className="size-4 animate-spin" /> : <RotateCcw className="size-4" />}
                {refundMutation.isPending ? '환불 처리 중' : '환불 확정'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
